// src/components/Services/Services_DigitalMarketing.jsx
import React from "react";
import { motion } from "framer-motion";
import digitalGif from "../images/photos/marketing.gif"; // ✅ Ensure correct path

const pageVariants = {
  initial: { rotateY: 90, opacity: 0 },
  in: { rotateY: 0, opacity: 1 },
  out: { rotateY: -90, opacity: 0 },
};

const pageTransition = {
  type: "spring",
  stiffness: 80,
  damping: 20,
};

const fadeUp = {
  hidden: { y: 10, opacity: 0 },
  visible: (i = 0) => ({
    y: 0,
    opacity: 1,
    transition: { delay: 0.06 * i },
  }),
};

export default function Services_DigitalMarketing() {
  const services = [
    "Android Zero Touch Enrollment",
    "Android Management",
    "Cloud Computing",
    "Cloud Management Service",
    "Cyber Security",
    "iOS Management",
    "Mobile Management",
    "IT Management",
    "IT Support",
    "Software Development",
    "Digital Marketing",
  ];

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
      style={{
        minHeight: "100vh",
        background: "#040d1fff",
        color: "#e5e9edff",
        padding: "24px",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* HERO SECTION */}
        <section
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "32px",
            alignItems: "center",
          }}
        >
          {/* LEFT - GIF */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={0}
            style={{ textAlign: "center" }}
          >
            <img
              src={digitalGif}
              alt="Digital Marketing"
              style={{
                width: "100%",
                maxWidth: 480,
                height: "auto",
                borderRadius: 12,
                boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
              }}
              loading="lazy"
            />
          </motion.div>

          {/* RIGHT - TEXT */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={1}
          >
            <h1
              style={{
                marginTop: 0,
                fontSize: "clamp(1.8rem, 3vw, 2.8rem)",
                color: "#6dd4e8ff",
              }}
            >
              Digital Marketing
            </h1>
            <p style={{ lineHeight: 1.6, color: "#e2e8f0", fontSize: "1rem" }}>
              Digital Marketing refers to the use of digital channels, platforms,
              and technologies to promote products, services, or brands to a
              target audience. It encompasses SEO, PPC, social media, content
              marketing, email campaigns, influencer partnerships, and more to
              drive business growth and connect with customers online.
            </p>

            <h3 style={{ marginTop: 16, color: "#53bdd2ff" }}>
              Key Components of Digital Marketing
            </h3>
            <ul style={{ lineHeight: 1.7, color: "#f1f5f9", paddingLeft: 18 }}>
              <li>
                <strong>SEO:</strong> keyword research, on-page optimization,
                link building, and content creation.
              </li>
              <li>
                <strong>PPC Advertising:</strong> ad campaigns, targeting, and
                budget management.
              </li>
              <li>
                <strong>Social Media Marketing:</strong> account management,
                paid campaigns, and engagement.
              </li>
              <li>
                <strong>Content Marketing:</strong> blogs, videos, infographics,
                and cross-channel promotions.
              </li>
              <li>
                <strong>Email Marketing:</strong> segmentation, automation, and
                personalized campaigns.
              </li>
              <li>
                <strong>Affiliate & Influencer Marketing:</strong> brand
                partnerships to increase reach.
              </li>
              <li>
                <strong>Online PR & CRO:</strong> brand reputation and conversion
                optimization.
              </li>
              <li>
                <strong>Mobile & Video Marketing:</strong> mobile-first
                strategies and video campaigns.
              </li>
            </ul>

            {/* Buttons */}
            <div
              style={{
                marginTop: 18,
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <a
                href="mailto:support@zyntro.in"
                style={{
                  background: "#f69f48ff",
                  color: "white",
                  padding: "10px 16px",
                  borderRadius: 8,
                  textDecoration: "none",
                  fontWeight: 600,
                  textAlign: "center",
                  flex: "1 1 auto",
                }}
              >
                Email Support
              </a>
              <a
                href="tel:+919956353236"
                style={{
                  border: "1px solid #4e4e54ff",
                  color: "#010101ff",
                  padding: "10px 16px",
                  borderRadius: 8,
                  textDecoration: "none",
                  fontWeight: 600,
                  background: "white",
                  textAlign: "center",
                  flex: "1 1 auto",
                }}
              >
                Call +91-9956353236
              </a>
            </div>
          </motion.div>
        </section>

        {/* OTHER SERVICES */}
        <section style={{ marginTop: 48 }}>
          <h3
            style={{
              color: "#4fe1f4ff",
              fontSize: "clamp(1.2rem, 2.5vw, 1.6rem)",
              marginBottom: 16,
              textAlign: "center",
            }}
          >
            Other Services
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 14,
            }}
          >
            {services.map((s) => (
              <div
                key={s}
                style={{
                  background: "#1e293b",
                  padding: 12,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: "#1e7d95ff",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                  }}
                >
                  {s.charAt(0)}
                </div>
                <span style={{ fontSize: 15, flex: 1 }}>{s}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* ✅ RESPONSIVE STYLES */}
      <style>{`
        @media (max-width: 900px) {
          section {
            grid-template-columns: 1fr !important;
            text-align: center;
          }
          section img {
            margin: 0 auto;
          }
          section h1 {
            margin-top: 20px;
          }
        }
        @media (max-width: 480px) {
          h1 {
            font-size: 1.8rem !important;
          }
          ul {
            font-size: 0.9rem !important;
          }
          a {
            flex: 1 1 100% !important;
          }
        }
      `}</style>
    </motion.div>
  );
}
